/**
 * ProtectedRoute Component
 *
 * A route guard component that checks authentication and authorization
 * before rendering child components.
 *
 * Features:
 * - Checks if user is authenticated
 * - Checks if user has access to the specific route (via pagesAccessible)
 * - Redirects to /forbidden if access is denied
 * - Works with React Router's route protection pattern
 *
 * Ported from Vue router.beforeEach guard
 *
 * @example
 * ```tsx
 * // In route configuration:
 * <Route
 *   path="/booking"
 *   element={
 *     <ProtectedRoute route="/booking">
 *       <BookingPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "@/hooks/auth/useUser";
import { useAuth } from "@/hooks/auth/useAuth";
import { isProtectedRoute } from "@/router/routes";
import type { ProtectedRouteProps } from "./types";

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  route,
  redirectTo = "/forbidden",
}) => {
  const location = useLocation();
  const { isAuthenticated, hasAccessTo } = useUser();
  const { isLoading } = useAuth();

  // Determine which route to check
  const routeToCheck = route ?? location.pathname;

  // While auth is loading, show nothing (or could show a spinner)
  if (isLoading) {
    return null;
  }

  // If not authenticated, MSAL will handle the redirect
  // The auth hook will trigger login redirect automatically
  if (!isAuthenticated) {
    // Auth is still initializing or user needs to login
    return null;
  }

  // Check route access if this is a protected route
  if (isProtectedRoute(routeToCheck)) {
    const hasAccess = hasAccessTo(routeToCheck);

    if (!hasAccess) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Access granted, render children
  return <>{children}</>;
};

ProtectedRoute.displayName = "ProtectedRoute";
